"use client";

import React, { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useLocale } from "@/context/LocaleContext";

export default function ReminderEventFeature() {
  const { t } = useLocale(); 
  const carouselRef = useRef<HTMLDivElement>(null);
  
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 20);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 20);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
        checkScrollPosition();
    }, 100);

    window.addEventListener('resize', checkScrollPosition);
    return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', checkScrollPosition);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 340; 
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollPosition, 300);
    }
  };

  return (
    <div className="w-full">
      <section className="w-full bg-gray-50 py-16 md:-mt-10 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[45%] flex justify-center">
              <div className="relative w-full max-w-[500px] aspect-[3/4]">
                <Image 
                  src="/images/cta/custom-rsvp.webp" 
                  alt="Cara kerja reminder" 
                  fill 
                  className="object-contain drop-shadow-2xl" 
                />
              </div>
            </div>

            <div className="w-full md:w-[55%]">
              <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
                {t("EventReminder.how_it_works") || "Bagaimana cara kerjanya?"}
              </h2>
              
              <div className="space-y-6 lg:space-y-8">
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">1.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("EventReminder.step1_title") || "Tamu Mengisi RSVP"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[17px]">
                      {t("EventReminder.step1_desc") || "Hanya tamu yang sudah mengonfirmasi kehadiran yang akan menerima pengingat."}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">2.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("EventReminder.step2_title") || "Pengingat Dikirim Otomatis"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[17px]">
                      {t("EventReminder.step2_desc") || "Sistem akan mengirimkan reminder secara otomatis sesuai jadwal yang telah ditentukan."}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">3.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("EventReminder.step3_title") || "Melalui Whatsapp & Email"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[17px]">
                      {t("EventReminder.step3_desc") || "Pengingat dikirim melalui dua kanal utama untuk memastikan pesan tersampaikan dengan baik."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="w-full bg-white py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 text-center">
          
          <h2 className="text-3xl md:text-4xl lg:text-[2.4rem] font-extrabold text-gray-900 mb-4 tracking-tight">
            {t("EventReminder.why_important_title") || "Kenapa Fitur ini penting?"}
          </h2>
          <p className="text-gray-700 text-[16px] md:text-[22px] mb-12 font-normal">
            {t("EventReminder.without_reminder_desc") || "Tanpa pengingat, sering terjadi:"}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-[#FAFAFA] rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 mb-4 relative opacity-80 text-[#E85C57]">
                <Image src="/images/icons/alarm.svg" alt="Lupa tanggal" fill className="object-contain" />
              </div>
              <p 
                className="text-gray-800 font-semibold text-[15px] md:text-[16px] leading-snug"
                dangerouslySetInnerHTML={{ __html: t("EventReminder.problem_1") || "Tamu lupa tanggal<br/>atau jam acara" }}
              />
            </div>

            <div className="bg-[#FAFAFA] rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 mb-4 relative opacity-80 text-[#E85C57]">
                <Image src="/images/icons/note.svg" alt="Tidak sesuai konfirmasi" fill className="object-contain" />
              </div>
              <p 
                className="text-gray-800 font-semibold text-[15px] md:text-[16px] leading-snug"
                dangerouslySetInnerHTML={{ __html: t("EventReminder.problem_2") || "Kehadiran tidak sesuai<br/>dengan konfirmasi" }}
              />
            </div>

            <div className="bg-[#FAFAFA] rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 mb-4 relative opacity-80 text-[#E85C57]">
                <Image src="/images/icons/person.svg" alt="Follow up manual" fill className="object-contain" />
              </div>
              <p 
                className="text-gray-800 font-semibold text-[15px] md:text-[16px] leading-snug"
                dangerouslySetInnerHTML={{ __html: t("EventReminder.problem_3") || "Penyelenggara harus<br/>melakukan follow-up manual" }}
              />
            </div>
          </div>

          <p 
            className="text-gray-700 font-medium text-[16px] md:text-[18px]"
            dangerouslySetInnerHTML={{ __html: t("EventReminder.solution_desc") || "Reminder Acara Otomatis membantu<br /> mengatasi semua hal tersebut." }}
          />
          
        </div>
      </section>

      <section className="w-full bg-gray-50 md:-mb-20 py-16 md:py-24 border-t border-gray-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
          
          <h2 className="text-3xl md:text-4xl lg:text-[2.8rem] font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            {t("EventReminder.main_benefits_title") || "Manfaat utama"}
          </h2>

          <div className="relative group">
            
            {/* Tombol Kiri */}
            <button 
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border shadow-lg flex items-center justify-center transition-all duration-300
                ${canScrollLeft 
                  ? "bg-white border-gray-200 text-gray-700 hover:text-[#E85C57] hover:border-[#E85C57] cursor-pointer" 
                  : "bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed opacity-50"
                }`}
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Container Carousel dengan event onScroll */}
            <div 
              ref={carouselRef}
              onScroll={checkScrollPosition}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-8 pt-4 px-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} 
            >
              {/* Card 1 */}
              <div className="min-w-[300px] md:min-w-[360px] lg:min-w-[380px] bg-white rounded-[32px] p-8 md:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100/80 snap-start flex flex-col items-center text-center">
                <div className="w-[140px] h-[140px] rounded-full bg-[#F9F9F9] flex items-center justify-center mb-8 hover:scale-105 transition-transform duration-300">
                  <div className="w-[75px] h-[75px] relative">
                    <Image src="/images/features/landing-page/forget-email.webp" alt="Mengurangi lupa" fill className="object-contain" />
                  </div>
                </div>
                <h3 
                  className="text-[1.2rem] md:text-[1.3rem] font-bold text-gray-900 mb-4 leading-snug"
                  dangerouslySetInnerHTML={{ __html: t("EventReminder.card1_title") || "Mengurangi Tamu yang<br class='hidden md:block' /> Lupa Hadir" }}
                />
                <p className="text-gray-700 text-[16px] leading-relaxed">
                  {t("EventReminder.card1_desc") || "Tamu mendapatkan pengingat tepat waktu sehingga lebih siap menghadiri acara."}
                </p>
              </div>

              {/* Card 2 */}
              <div className="min-w-[300px] md:min-w-[360px] lg:min-w-[380px] bg-white rounded-[32px] p-8 md:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100/80 snap-start flex flex-col items-center text-center">
                <div className="w-[140px] h-[140px] rounded-full bg-[#F9F9F9] flex items-center justify-center mb-8 hover:scale-105 transition-transform duration-300">
                  <div className="w-[75px] h-[75px] relative">
                    <Image src="/images/features/landing-page/amplop-digital.webp" alt="Tingkat kehadiran" fill className="object-contain" />
                  </div>
                </div>
                <h3 
                  className="text-[1.2rem] md:text-[1.3rem] font-bold text-gray-900 mb-4 leading-snug"
                  dangerouslySetInnerHTML={{ __html: t("EventReminder.card2_title") || "Meningkatkan Tingkat<br class='hidden md:block' /> Kehadiran" }}
                />
                <p className="text-gray-700 text-[16px] leading-relaxed">
                  {t("EventReminder.card2_desc") || "Dengan reminder yang konsisten, kemungkinan tamu benar-benar datang sesuai rsvp menjadi lebih tinggi."}
                </p>
              </div>

              {/* Card 3 */}
              <div className="min-w-[300px] md:min-w-[360px] lg:min-w-[380px] bg-white rounded-[32px] p-8 md:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100/80 snap-start flex flex-col items-center text-center">
                <div className="w-[140px] h-[140px] rounded-full bg-[#F9F9F9] flex items-center justify-center mb-8 hover:scale-105 transition-transform duration-300">
                  <div className="w-[75px] h-[75px] relative">
                    <Image src="/images/features/landing-page/auto-follow.webp" alt="Tanpa follow up" fill className="object-contain" />
                  </div>
                </div>
                <h3 className="text-[1.2rem] md:text-[1.3rem] font-bold text-gray-900 mb-4 leading-snug">
                  {t("EventReminder.card3_title") || "Tanpa Follow-up Manual"}
                </h3>
                <p className="text-gray-700 text-[16px] leading-relaxed mb-3">
                  {t("EventReminder.card3_desc_1") || "Kamu tidak perlu lagi:"}
                </p>
                <ul className="text-left list-disc pl-5 text-gray-700 text-[15px] leading-relaxed space-y-1">
                  <li>{t("EventReminder.card3_li_1") || "Mengirim pesan pengingat satu per satu"}</li>
                  <li>{t("EventReminder.card3_li_2") || "Menghubungi tamu menjelang hari acara"}</li>
                </ul>
                <p className="text-gray-700 text-[16px] mt-4 font-normal">
                  {t("EventReminder.card3_desc_2") || "Semua sudah diurus oleh sistem."}
                </p>
              </div>

              {/* Card 4 */}
              <div className="min-w-[300px] md:min-w-[360px] lg:min-w-[380px] bg-white rounded-[32px] p-8 md:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100/80 snap-start flex flex-col items-center text-center">
                <div className="w-[140px] h-[140px] rounded-full bg-[#F9F9F9] flex items-center justify-center mb-8 hover:scale-105 transition-transform duration-300">
                  <div className="w-[95px] h-[95px] relative">
                    <Image src="/images/features/landing-page/integrasi-feature.webp" alt="Terintegrasi" fill className="object-contain" />
                  </div>
                </div>
                <h3 className="text-[1.2rem] md:text-[1.3rem] font-bold text-gray-900 mb-4 leading-snug">
                  {t("EventReminder.card4_title") || "Terintegrasi dengan Sistem Undangan"}
                </h3>
                <p className="text-gray-700 text-[16px] leading-relaxed mb-3">
                  {t("EventReminder.card4_desc_1") || "Reminder acara otomatis bekerja selaras dengan:"}
                </p>
                <ul className="text-left list-disc pl-5 text-gray-700 text-[15px] leading-relaxed space-y-1">
                  <li className='font-semibold'>{t("EventReminder.card4_li_1") || "Custom rsvp"}</li>
                  <li className='font-semibold'>{t("EventReminder.card4_li_2") || "Sebar otomatis via whatsapp"}</li>
                  <li className='font-semibold'>{t("EventReminder.card4_li_3") || "Qr code check-in buku tamu"}</li>
                </ul>
                <p className="text-gray-700 text-[16px] mt-4 font-normal">
                  {t("EventReminder.card4_desc_2") || "Data kehadiran dan pengingat selalu sinkron dalam satu sistem."}
                </p>
              </div>

              {/* Card 5 */}
              <div className="min-w-[300px] md:min-w-[360px] lg:min-w-[380px] bg-white rounded-[32px] p-8 md:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100/80 snap-start flex flex-col items-center text-center">
                <div className="w-[140px] h-[140px] rounded-full bg-[#F9F9F9] flex items-center justify-center mb-8 hover:scale-105 transition-transform duration-300">
                  <div className="w-[75px] h-[75px] relative">
                    <Image src="/images/icons/groups.svg" alt="Cocok digunakan" fill className="object-contain" />
                  </div>
                </div>
                <h3 className="text-[1.2rem] md:text-[1.3rem] font-bold text-gray-900 mb-4 leading-snug">
                  {t("EventReminder.card5_title") || "Cocok Digunakan Untuk"}
                </h3>
                <ul className="text-left list-disc pl-5 text-gray-700 text-[15px] leading-relaxed space-y-2 mb-4">
                  <li>{t("EventReminder.card5_li_1") || "Pernikahan & intimate wedding"}</li>
                  <li>{t("EventReminder.card5_li_2") || "Acara keluarga"}</li>
                  <li>{t("EventReminder.card5_li_3") || "Event komunitas"}</li>
                  <li>{t("EventReminder.card5_li_4") || "Acara profesional & korporasi"}</li>
                </ul>
                <p className="text-gray-700 text-[16px] mt-4 font-normal">
                  {t("EventReminder.card5_desc") || "Fitur ini membantu semua jenis acara berjalan lebih tertib."}
                </p>
              </div>

            </div>

            {/* Tombol Kanan */}
            <button 
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border shadow-lg flex items-center justify-center transition-all duration-300
                ${canScrollRight 
                  ? "bg-white border-gray-200 text-gray-700 hover:text-[#E85C57] hover:border-[#E85C57] cursor-pointer" 
                  : "bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed opacity-50"
                }`}
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

          </div>
        </div>
      </section>

    </div>
  );
}